import { useMemo } from 'react';
import { useIntl } from 'umi';
import { formatLocaleText } from '@home/components/CascaderSelect/utils';
import { getRegionOptions, getSelectedCountry, REGION_CASCADER_SELECT_LOCALE_KEYS } from './utils';

import type { ReactNode } from 'react';
import type { IRegion } from '@home/types/common';

interface IUseRegionCascaderSelectParams {
  locale?: any;
  label?: string | ReactNode;
  placeholder?: string;
  multiCountryText?: ReactNode;
  regions: IRegion[];
  value?: string;
}

export default function useRegionCascaderSelect(params: IUseRegionCascaderSelectParams) {
  const {
    locale,
    label = REGION_CASCADER_SELECT_LOCALE_KEYS.label,
    placeholder = REGION_CASCADER_SELECT_LOCALE_KEYS.placeholder,
    multiCountryText = REGION_CASCADER_SELECT_LOCALE_KEYS.multiCountryText,
    regions = [],
    value,
  } = params;
  const { locale: intlLocale, formatMessage } = useIntl();
  const currentLocale = locale || intlLocale;
  const labelText = formatLocaleText(formatMessage, label);
  const placeholderText = formatLocaleText(formatMessage, placeholder) as string;
  const multiCountryTextContent = formatLocaleText(formatMessage, multiCountryText);
  const firstLevelTitleText = formatLocaleText(formatMessage, REGION_CASCADER_SELECT_LOCALE_KEYS.firstLevelTitle);
  const secondLevelTitleText = formatLocaleText(formatMessage, REGION_CASCADER_SELECT_LOCALE_KEYS.secondLevelTitle);

  const options = useMemo(() => {
    return getRegionOptions(regions, currentLocale);
  }, [regions, currentLocale]);

  const selectedContext = useMemo(() => {
    return getSelectedCountry(regions, value);
  }, [regions, value]);

  return {
    currentLocale,
    labelText,
    placeholderText,
    multiCountryTextContent,
    firstLevelTitleText,
    secondLevelTitleText,
    options,
    selectedContext,
  };
}
